"use client";

import React, { useEffect, useRef, useState, useMemo } from "react";
import dynamic from "next/dynamic";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Globe } from "lucide-react";
import { NodeBasicInfo } from "@/contexts/NodeListContext";

// Dynamically import Globe to avoid SSR issues
const GlobeGL = dynamic(() => import("react-globe.gl"), {
    ssr: false,
    loading: () => (
        <div className="w-full h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
    ),
});

// Map emoji flags to ISO 3166-1 alpha-2 country codes
function emojiToCountryCode(emoji: string): string | null {
    if (!emoji || emoji.length < 2) return null;

    // Regional indicator symbols range from 🇦 (U+1F1E6) to 🇿 (U+1F1FF)
    const codePoints = [...emoji].map(c => c.codePointAt(0) || 0);

    // Filter to only regional indicator symbols
    const regionalIndicators = codePoints.filter(cp => cp >= 0x1F1E6 && cp <= 0x1F1FF);

    if (regionalIndicators.length >= 2) {
        const firstLetter = String.fromCharCode(regionalIndicators[0] - 0x1F1E6 + 65);
        const secondLetter = String.fromCharCode(regionalIndicators[1] - 0x1F1E6 + 65);
        return firstLetter + secondLetter;
    }

    return null;
}

// Color palette for different countries
const COUNTRY_COLORS = [
    "#FF6B6B", // Red
    "#4ECDC4", // Teal
    "#45B7D1", // Blue
    "#96CEB4", // Green
    "#FFEAA7", // Yellow
    "#DDA0DD", // Plum
    "#98D8C8", // Mint
    "#F7DC6F", // Gold
    "#BB8FCE", // Purple
    "#85C1E9", // Sky Blue
    "#F8B500", // Orange
    "#58D68D", // Lime
    "#EC7063", // Coral
    "#5DADE2", // Cyan
    "#AF7AC5", // Violet
];

interface ServerGlobeDialogProps {
    nodeList: NodeBasicInfo[];
    onlineNodes?: string[];
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export function ServerGlobeDialog({ nodeList, onlineNodes, open, onOpenChange }: ServerGlobeDialogProps) {
    const { t } = useTranslation();
    const globeRef = useRef<any>(null);
    const [countries, setCountries] = useState<any>({ features: [] });
    const [internalOpen, setInternalOpen] = useState(false);
    const [globeReady, setGlobeReady] = useState(false);

    // Support both controlled and uncontrolled modes
    const isOpen = open !== undefined ? open : internalOpen;
    const setIsOpen = onOpenChange !== undefined ? onOpenChange : setInternalOpen;

    // Load GeoJSON data for countries - use 50m for higher resolution (includes Hong Kong)
    useEffect(() => {
        if (isOpen && countries.features.length === 0) {
            fetch("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json")
                .then((res) => res.json())
                .then((worldData) => {
                    import("topojson-client").then((topojson) => {
                        const countriesGeo = topojson.feature(
                            worldData,
                            worldData.objects.countries
                        );
                        setCountries(countriesGeo);
                    });
                })
                .catch(console.error);
        }
    }, [isOpen, countries.features.length]);

    // Extract unique countries from node list with counts
    const serverCountries = useMemo(() => {
        const countryMap = new Map<string, { count: number; nodes: string[]; color: string }>();
        let colorIndex = 0;

        nodeList.forEach((node) => {
            const countryCode = emojiToCountryCode(node.region);
            if (countryCode) {
                const existing = countryMap.get(countryCode);
                if (existing) {
                    existing.count++;
                    existing.nodes.push(node.name);
                } else {
                    countryMap.set(countryCode, {
                        count: 1,
                        nodes: [node.name],
                        color: COUNTRY_COLORS[colorIndex % COUNTRY_COLORS.length],
                    });
                    colorIndex++;
                }
            }
        });

        return countryMap;
    }, [nodeList]);

    // Map country names to ISO codes (from Natural Earth data)
    const countryNameToCode: Record<string, string> = useMemo(() => ({
        "United States of America": "US",
        "United Kingdom": "GB",
        "China": "CN",
        "Japan": "JP",
        "South Korea": "KR",
        "Germany": "DE",
        "France": "FR",
        "Netherlands": "NL",
        "Singapore": "SG",
        "Hong Kong": "HK",
        "Taiwan": "TW",
        "Australia": "AU",
        "Canada": "CA",
        "Russia": "RU",
        "India": "IN",
        "Brazil": "BR",
        "Sweden": "SE",
        "Finland": "FI",
        "Norway": "NO",
        "Denmark": "DK",
        "Poland": "PL",
        "Switzerland": "CH",
        "Italy": "IT",
        "Spain": "ES",
        "Portugal": "PT",
        "Ireland": "IE",
        "Belgium": "BE",
        "Austria": "AT",
        "Czech Republic": "CZ",
        "Czechia": "CZ",
        "Romania": "RO",
        "Bulgaria": "BG",
        "Hungary": "HU",
        "Ukraine": "UA",
        "Turkey": "TR",
        "Israel": "IL",
        "United Arab Emirates": "AE",
        "Saudi Arabia": "SA",
        "South Africa": "ZA",
        "Mexico": "MX",
        "Argentina": "AR",
        "Chile": "CL",
        "Colombia": "CO",
        "Peru": "PE",
        "Indonesia": "ID",
        "Malaysia": "MY",
        "Thailand": "TH",
        "Vietnam": "VN",
        "Philippines": "PH",
        "New Zealand": "NZ",
        "Egypt": "EG",
        "Nigeria": "NG",
        "Kenya": "KE",
        "Morocco": "MA",
        "Luxembourg": "LU",
        "Iceland": "IS",
        "Greece": "GR",
        "Croatia": "HR",
        "Slovenia": "SI",
        "Slovakia": "SK",
        "Estonia": "EE",
        "Latvia": "LV",
        "Lithuania": "LT",
        "Moldova": "MD",
        "Belarus": "BY",
        "Georgia": "GE",
        "Armenia": "AM",
        "Azerbaijan": "AZ",
        "Kazakhstan": "KZ",
        "Uzbekistan": "UZ",
        "Pakistan": "PK",
        "Bangladesh": "BD",
        "Sri Lanka": "LK",
        "Nepal": "NP",
        "Myanmar": "MM",
        "Cambodia": "KH",
        "Laos": "LA",
        "Greenland": "GL",
        "Macao": "MO",
        "Puerto Rico": "PR",
        "Guam": "GU",
    }), []);

    // Get polygon color based on whether country has servers
    const getPolygonColor = (feat: any) => {
        const countryName = feat.properties?.name || feat.properties?.NAME;
        const isoCode = countryNameToCode[countryName];

        if (isoCode && serverCountries.has(isoCode)) {
            return serverCountries.get(isoCode)!.color;
        }
        return "rgba(100, 100, 100, 0.1)";
    };

    // Get polygon altitude for highlighted countries
    const getPolygonAltitude = (feat: any) => {
        const countryName = feat.properties?.name || feat.properties?.NAME;
        const isoCode = countryNameToCode[countryName];

        if (isoCode && serverCountries.has(isoCode)) {
            return 0.02;
        }
        return 0.001;
    };

    // Get polygon side color
    const getPolygonSideColor = (feat: any) => {
        const countryName = feat.properties?.name || feat.properties?.NAME;
        const isoCode = countryNameToCode[countryName];

        if (isoCode && serverCountries.has(isoCode)) {
            const color = serverCountries.get(isoCode)!.color;
            return color + "AA";
        }
        return "rgba(100, 100, 100, 0.05)";
    };

    // Get polygon label
    const getPolygonLabel = (feat: any): string => {
        const countryName = feat.properties?.name || feat.properties?.NAME;
        const isoCode = countryNameToCode[countryName];

        if (isoCode && serverCountries.has(isoCode)) {
            const data = serverCountries.get(isoCode)!;
            return `
        <div style="padding: 8px; background: rgba(0,0,0,0.8); border-radius: 8px; color: white;">
          <div style="font-weight: bold; margin-bottom: 4px;">${countryName}</div>
          <div style="color: ${data.color};">${data.count} ${data.count > 1 ? t("globe.servers") : t("globe.server")}</div>
          <div style="font-size: 11px; margin-top: 4px; color: #aaa;">${data.nodes.slice(0, 3).join(", ")}${data.nodes.length > 3 ? ` +${data.nodes.length - 3}` : ""}</div>
        </div>
      `;
        }
        return "";
    };

    // Initialize globe settings - no auto-rotate as per user request
    useEffect(() => {
        if (globeRef.current && globeReady) {
            globeRef.current.controls().autoRotate = false;
            globeRef.current.pointOfView({ lat: 30, lng: 0, altitude: 2.5 });
        }
    }, [globeReady]);

    // Create legend data
    const legendItems = useMemo(() => {
        return Array.from(serverCountries.entries()).map(([code, data]) => ({
            code,
            ...data,
        }));
    }, [serverCountries]);

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <button
                    className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                    title={t("globe.view_globe")}
                >
                    <Globe className="h-4 w-4" />
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl w-[90vw] h-[80vh] flex flex-col p-0 overflow-hidden">
                <DialogHeader className="p-4 pb-0">
                    <DialogTitle className="flex items-center gap-2">
                        <Globe className="h-5 w-5" />
                        {t("globe.title")}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 relative min-h-0">
                    {isOpen && (
                        <GlobeGL
                            ref={globeRef}
                            globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
                            bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
                            backgroundImageUrl="//unpkg.com/three-globe/example/img/night-sky.png"
                            polygonsData={countries.features}
                            polygonCapColor={getPolygonColor}
                            polygonSideColor={getPolygonSideColor}
                            polygonAltitude={getPolygonAltitude}
                            polygonLabel={getPolygonLabel}
                            polygonStrokeColor={() => "#444"}
                            atmosphereColor="#3a82f7"
                            atmosphereAltitude={0.15}
                            width={800}
                            height={500}
                            onGlobeReady={() => setGlobeReady(true)}
                        />
                    )}

                    {/* Legend */}
                    {legendItems.length > 0 && (
                        <div className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm rounded-lg p-3 max-h-48 overflow-y-auto shadow-lg border">
                            <div className="text-xs font-semibold mb-2 text-muted-foreground">
                                {t("globe.legend")} ({legendItems.length} {t("globe.regions")})
                            </div>
                            <div className="space-y-1">
                                {legendItems.map((item) => (
                                    <div key={item.code} className="flex items-center gap-2 text-xs">
                                        <div
                                            className="w-3 h-3 rounded-sm"
                                            style={{ backgroundColor: item.color }}
                                        />
                                        <span className="font-mono">{item.code}</span>
                                        <span className="text-muted-foreground">
                                            ({item.count} {item.count > 1 ? t("globe.servers") : t("globe.server")})
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Instructions */}
                    <div className="absolute top-4 right-4 bg-background/80 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-muted-foreground">
                        {t("globe.instructions")}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
